import { Box, Dialog, DialogContent, IconButton, Typography } from '@material-ui/core';
import { Close as CloseIcon, NavigateBefore, NavigateNext } from '@material-ui/icons';
import { useCallback, useEffect, useState } from 'react';

const Lightbox = ({ open, onClose, images, initialIndex = 0, t }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentImage = images[currentIndex];

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const navigateLightbox = useCallback(
    (direction) => {
      setCurrentIndex((prevIndex) => {
        const newIndex =
          direction === 'next'
            ? (prevIndex + 1) % images.length
            : (prevIndex - 1 + images.length) % images.length;
        return newIndex;
      });
    },
    [images.length]
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!open) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') navigateLightbox('prev');
      if (e.key === 'ArrowRight') navigateLightbox('next');
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [open, navigateLightbox, onClose]);

  if (!currentImage) return null;

  const currentAltText = `${t(`gallery.filter.${currentImage.category}`)} ${currentIndex + 1}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="lightbox-image-title"
      aria-describedby="lightbox-image-description"
      PaperProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.95)',
          maxWidth: '95vw',
          maxHeight: '95vh',
          width: '100%',
          height: '100%',
        },
      }}
    >
      <DialogContent
        style={{
          padding: 0,
          position: 'relative',
          width: '100%',
          height: '100%',
          minHeight: '90vh',
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label={t('gallery.lightbox.close')}
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1300,
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }}
        >
          <CloseIcon />
        </IconButton>

        {images.length > 1 && (
          <>
            <IconButton
              onClick={() => navigateLightbox('prev')}
              aria-label={t('gallery.lightbox.previous')}
              style={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1300,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }}
            >
              <NavigateBefore fontSize="large" />
            </IconButton>

            <IconButton
              onClick={() => navigateLightbox('next')}
              aria-label={t('gallery.lightbox.next')}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1300,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              }}
            >
              <NavigateNext fontSize="large" />
            </IconButton>
          </>
        )}

        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          style={{
            width: '100%',
            height: '90vh',
            padding: '20px',
            boxSizing: 'border-box',
          }}
        >
          <img
            src={currentImage.src}
            alt={currentAltText}
            id="lightbox-image-title"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
          <span id="lightbox-image-description" className="sr-only">
            {t('gallery.lightbox.imageDescription', {
              current: currentIndex + 1,
              total: images.length,
              alt: currentAltText,
            })}
          </span>
        </Box>

        {images.length > 1 && (
          <Typography
            variant="body2"
            style={{
              position: 'absolute',
              bottom: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              padding: '4px 12px',
              borderRadius: '4px',
            }}
          >
            {currentIndex + 1} / {images.length}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Lightbox;
